import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const BOOK_COVER = "https://media-manager-c.questera.ai/greta-media/ae05bbc051fb17176d2c348bf77438a7a494626431a725f22ebe70ac4579d949c16512ea83019a888cf26e0e50680ce7/images/aW1hZ2UvanBlZw==/b61a60ddab35f9b0af6b0aead5859bb1.jpg";

export default function About() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

        <div className="flex flex-col lg:flex-row gap-14 items-start mb-16">
          <div className="lg:w-2/3">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">About Website Blueprint Foundation: Volume 1</h1>
            <p className="text-xl text-slate-700 font-medium leading-relaxed mb-6">
              A complete beginner-friendly guide to planning, building, and owning a professional website — written for readers who may have an idea, a business, a project, or a message that deserves a proper home online, but who don't come from a development background.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              The path is clear, honest, and durable. No shortcuts. No platform lock-in. No assumptions about prior knowledge.
            </p>
          </div>
          <div className="lg:w-1/3 flex justify-center lg:justify-end">
            <img src={BOOK_COVER} alt="Website Blueprint Foundation Book Cover" className="w-full max-w-[220px] h-auto rounded-xl shadow-xl border border-slate-200" />
          </div>
        </div>

        <div className="prose prose-lg prose-blue max-w-none text-slate-600">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8 rounded-r-lg not-prose">
            <p className="text-blue-900 font-medium italic text-lg leading-relaxed">
              "We don't do shortcuts. We build foundations. A website built on a strong foundation will outlast any trend or platform update."
            </p>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">What the Book Covers</h2>

          <div className="not-prose space-y-6 mb-10">
            {[
              { num: "Chapter 1", title: "Before You Build", desc: "Domain ownership, hosting types, site structure planning, honest budget breakdowns, and platform selection based on ownership, search needs, and long-term flexibility — not marketing promises." },
              { num: "Chapter 2", title: "HTML and CSS From Scratch", desc: "How the web works, how HTML provides structure, how CSS controls presentation. Readers build a complete multi-page website with responsive design, semantic markup, forms, typography, layout systems, and validation." },
              { num: "Chapter 3", title: "WordPress Setup", desc: "From installation to launch. Essential settings, HTTPS, permalink structure, theme selection, lean plugin strategy, SEO plugin setup, XML sitemaps, Google Search Console, backups, security hardening, and pre-launch auditing." },
            ].map((ch, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl border border-slate-200 p-6 flex gap-5 items-start">
                <div className="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0 mt-0.5">{ch.num}</div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg mb-1">{ch.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{ch.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">The Ownership Philosophy</h2>
          <p>
            When you build a business, you wouldn't let your landlord own the rights to your store's name, interior design, and customer list. Yet millions of people do exactly that when they use proprietary website builders. This book is fundamentally about <strong>ownership</strong> — registering your own domain, choosing independent hosting, and understanding the core code that powers the web.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">What Makes This Different</h2>
          <ul className="space-y-3 list-none pl-0 not-prose">
            {[
              { icon: FiIcons.FiZap, text: "No Assumed Knowledge — starts at zero, explains everything." },
              { icon: FiIcons.FiCheckSquare, text: "Annotated Checklist Format — every section ends with actionable checklists so you're building, not just reading." },
              { icon: FiIcons.FiEye, text: "Accessibility from Chapter One — WCAG 2.2 is a standard, not an afterthought." },
              { icon: FiIcons.FiLayout, text: "WordPress Done Right — open-source, lean, configured for performance and security." },
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <SafeIcon icon={item.icon} className="text-blue-600 mt-0.5 flex-shrink-0 text-lg" />
                <span className="text-slate-700">{item.text}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">The Result</h2>
          <p>
            By the time you finish this volume, you will have planned, designed, coded, and launched a custom, professional website. More importantly — if something breaks, you'll know exactly how to fix it. The result is not just a finished site, but understanding.
          </p>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row gap-4">
          <Link to="/buy" className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-700 transition-colors">
            Buy the Book <SafeIcon icon={FiIcons.FiArrowRight} />
          </Link>
          <Link to="/learn" className="inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-700 px-8 py-4 rounded-lg font-semibold hover:bg-slate-50 transition-colors">
            See What You'll Learn
          </Link>
        </div>
      </motion.div>
    </div>
  );
}